
import React from 'react';
import debounce from 'lodash/debounce';
import { FormattedMessage } from 'react-intl';
import { TextField } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomTooltip from '__common/components/CustomTooltip';
import ConfirmationTextField from '__common/components/ConfirmationTextField';

type Props = {
  field: numberBlock;
  index: number;
  updateProjectOption: (field: string, value: any) => void;
  value: any;
  type?: string;
  tooltip?: string;
  validationOnBlur: any;
  error?: string;
  warn?: string,
  step?: number;
  disable?: boolean;
};

type State = {
  val: string | number,
};

export default class NumberField extends React.PureComponent<Props, State> {
  showTooltipAfterWordLength = 20;

  state = {
    val: this.props.type === 'text' ? '' : 0,
  };

  constructor(props: Props) {
    super(props);
    this.onChange = debounce(this.onChange, 250);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.value !== this.props.value) {
      this.setState({ val: this.props.value });
    }
  }

  tooltipButton = () => {
    const { field } = this.props;
    if (!field.tooltip) return null;
    try {
      const content = typeof field.tooltip === 'function' ? field.tooltip() : field.tooltip;
      if (!content || typeof content !== 'string') return null;
      return (
        <CustomTooltip title={content} isHtml placement="right">
          <InfoOutlinedIcon className="dsp-field-tooltip-icon" />
        </CustomTooltip>
      );
    } catch {
      return null;
    }
  }

  componentDidMount() {
    const { value } = this.props;
    this.setState({ val: value });
  }

  setVal = (apiField: string, val: string | number) => {
    this.setState({ val });
    this.onChange(val, apiField);
  }

  onChange = (val: string | number, apiField: string) => {
    this.props.updateProjectOption(apiField, val);
  }

  tooltipedTextField = () => {
    const { val } = this.state;
    const { field, type, validationOnBlur, error } = this.props;
    const isInvalid = Boolean(field.missing || error);
    let tooltipVal = val;

    if (typeof val === 'string' && val.length > this.showTooltipAfterWordLength) {
      tooltipVal = '';
    }
    
    return (
      // <Tooltip title={tooltipVal} placement="bottom">
      //   <div className="field-container">
      //     <TextField
      //       id={field.label}
      //       value={val}
      //       onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setVal(field.apiField, event.target.value)}
      //       size="small"
      //       type={type || 'number'}
      //       fullWidth={true}
      //       min={field.min}
      //       max={field.max}
      //       onBlur={validationOnBlur}
      //       key={`${field.apiField}-inside`}
      //       disabled={this.props.disable ? this.props.disable : false}
      //     />
      //   </div>
      // </Tooltip>
      <TextField
        id={field.label}
        value={val}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setVal(field.apiField, event.target.value)}
        size="medium"
        type={type || 'number'}
        inputProps={{
          min: field.min,
          max: field.max,
          step: field.step || (type === 'number' ? 1 : undefined),
          'aria-invalid': isInvalid || undefined,
        }}
        onBlur={validationOnBlur}
        key={`${field.apiField}-inside`}
        disabled={this.props.disable ? this.props.disable : false}
        title={String(tooltipVal)}
        variant="standard"
        fullWidth
        error={isInvalid}
      />
    );
  }

  textField = () => {
    const { field, updateProjectOption, value, type, validationOnBlur, error } = this.props;
    const isInvalid = Boolean(field.missing || error);
    return (
      <TextField
        id={field.label}
        value={value}
        className="number-field"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateProjectOption(field.apiField, event.target.value)}
        size="small"
        type={type || 'number'}
        inputProps={{
          min: field.min,
          max: field.max,
          step: field.step || (type === 'number' ? 1 : undefined),
          'aria-invalid': isInvalid || undefined,
        }}
        fullWidth={true}
        onBlur={validationOnBlur}
        key={`${field.apiField}-inside`}
        disabled={this.props.disable ? this.props.disable : false}
        variant="standard"
        error={isInvalid}
      />
    );
  }

  confirmTextField = () => {
    const { field, value, type, validationOnBlur, updateProjectOption, error } = this.props;
    const isInvalid = Boolean(field.missing || error);
    return (
          <ConfirmationTextField
            value={value ?? ""}
            onConfirm={(value) => updateProjectOption(field.apiField, value)}
            title={field.title}
            content={field.content}
            shouldConfirm={field.shouldConfirm}
            shouldConfirmCb={field.shouldConfirmCb}
            textFieldProps={{
              id: field.label,
              value: value,
              className: "number-field",
              type: type || 'number',
              inputProps: {
                step: field.step,
                min: field.min,
                max: field.max,
                'aria-invalid': isInvalid || undefined,
              },
              fullWidth: true,
              onBlur: validationOnBlur,
              key: `${field.apiField}-inside`,
              variant: "standard",
              size: "small",
              error: isInvalid,
            }}
          />
    );
  }

  renderField = (shouldConfirm: boolean, tooltip) => {
    if (shouldConfirm) {
      return this.confirmTextField();
    }
    else if (tooltip) {
      return this.tooltipedTextField();
    }
    return this.textField();
}

  render() {
    const { field, index, tooltip, error, warn } = this.props;
    const labelContent = field.label ? <FormattedMessage id={field.label} /> : '';
    const tooltipIcon = this.tooltipButton();

    return (
      <div className="field" key={`text-${index}`}>
        {field.label && (
          tooltipIcon ? (
            <div className="dsp-label-row">
              <label id={field.id} className={`input-label ${field.missing || error ? 'input-warning' : ''} ${field.labelClassName || ''}`}>
                {labelContent}
              </label>
              {tooltipIcon}
            </div>
          ) : (
            <label id={field.id} className={`input-label ${field.missing || error ? 'input-warning' : ''} ${field.labelClassName || ''}`}>
              {labelContent}
            </label>
          )
        )}
        {this.renderField(field.shouldConfirm, tooltip)}
        {error && <div className="input-warning">{error}</div>}
        {field.note && field.note.shouldShow && field.note.message && <div className="note" dangerouslySetInnerHTML={{ __html: field.note.message}}></div>}
        {warn && <div className="input-soft-warning">{warn}</div>}
      </div>
    );
  }
}
